import { useEffect, useState } from 'react'
import { chatApi } from '../../api/chatApi'
import { ChatConversation } from '../../types'
import { toast } from 'react-hot-toast'
import { Loader2, MessageCircle } from 'lucide-react'
import { Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'

export default function ChatPage() {
  const [conversations, setConversations] = useState<ChatConversation[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    chatApi
      .getConversations()
      .then(({ data }) => setConversations(data.data?.content || []))
      .catch(() => toast.error('Failed to load conversations'))
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 size={28} className="animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-xl font-bold text-gray-900 mb-6">Messages</h1>

      {conversations.length === 0 ? (
        <div className="card text-center py-16 text-gray-400">
          <MessageCircle size={40} className="mx-auto mb-3 opacity-30" />
          <p>No conversations yet.</p>
          <p className="text-sm mt-1">Connect with employees to start chatting.</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
          {conversations.map((conv) => (
            <Link
              key={conv.conversationId}
              to={`/chat/${conv.conversationId}`}
              state={{ otherUser: conv.otherUser }}
              className="flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors no-underline"
            >
              <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center overflow-hidden shrink-0">
                {conv.otherUser.profilePicUrl ? (
                  <img src={conv.otherUser.profilePicUrl} alt={conv.otherUser.displayName} className="w-full h-full object-cover" />
                ) : (
                  <span className="font-medium text-primary-700">
                    {conv.otherUser.displayName?.[0]?.toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between">
                  <p className="font-medium text-gray-900">{conv.otherUser.displayName}</p>
                  {conv.lastMessageTime && (
                    <p className="text-xs text-gray-400 shrink-0 ml-2">
                      {formatDistanceToNow(new Date(conv.lastMessageTime), { addSuffix: true })}
                    </p>
                  )}
                </div>
                {conv.lastMessage && (
                  <p className="text-sm text-gray-500 truncate">{conv.lastMessage}</p>
                )}
              </div>
              {conv.unreadCount > 0 && (
                <span className="w-5 h-5 bg-primary-600 text-white text-xs rounded-full flex items-center justify-center shrink-0">
                  {conv.unreadCount}
                </span>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
